import {
  createContext,
  useContext,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { FeedIcon } from './FeedIcon';
import { FeedIconCheck } from './FeedIconCheck';
import { FeedRowAction } from './FeedRowActions';
import { FeedRowTitle } from './FeedRowTitle';
import {
  FEED_CHIP_CLASS,
  FEED_DESCRIPTION_CLASS,
  FEED_ROW_GRID,
  FEED_ROW_LINE_CLASS,
  FEED_ROW_META_CLASS,
  STAKE_PILL_CLASS,
} from './feedStyles';
import { FeedSubtasksChip } from './FeedSubtasksChip';
import { useRowCheck } from './useRowCheck';

import type { FeedRowView } from './feedRowView';
import type { ChildProgress } from '@littlebird/action-cards';

type FeedFrameHeaderProps = {
  view: Pick<FeedRowView, 'title' | 'stake' | 'failed'>;
  // What happens (`/happens`); a card with no object, or a row served before the fact, keeps its title
  happens?: string | null;
  title?: string;
  label?: string;
  // A note only staff see, such as the backend that made the card; it follows the label
  staffLabel?: ReactNode;
  struck?: boolean;
  subtasks?: ChildProgress;
  description?: string;
  icon: ReactNode;
  // Checks the card off from its icon, as the collapsed row does, so a click there does not close the card
  onCheck?: () => unknown;
  checkKind?: 'box' | 'mark';
  checkLabel?: string;
  onClose: () => void;
  // The compact row opened from its chevron, so the header closes it with that chevron turned up
  collapses?: boolean;
  actions?: ReactNode;
  // The ⋯ button before Close
  more?: ReactNode;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
};

// False where the host draws its own Close around the card, as the Board's panel does, so the header has none
export const FeedFrameClosableContext = createContext(true);

// The frame's header is the row it opened from: the tile, title, label and stake stay where the row drew them,
// so opening a card never moves its title. The reason follows under the title, and ⋯ and Close take the age's place
export const FeedFrameHeader = ({
  view,
  happens,
  title,
  label,
  staffLabel,
  struck = false,
  subtasks,
  description,
  icon,
  onCheck,
  checkKind = 'box',
  checkLabel = 'Mark done',
  onClose,
  collapses = false,
  actions,
  more,
  onKeyDown,
}: FeedFrameHeaderProps) => {
  const closable = useContext(FeedFrameClosableContext);
  const { checking, check } = useRowCheck(onCheck);
  const shownTitle = title ?? happens ?? view.title;
  return (
    <div
      data-testid="feed-frame-header"
      data-described={description ? '' : undefined}
      className={cn(FEED_ROW_GRID, 'min-h-0 items-start pb-0')}
    >
      <span className="mt-[calc(2*var(--ac-u))]">
        {check ? (
          <FeedIconCheck
            title={shownTitle}
            kind={checkKind}
            label={checkLabel}
            failed={view.failed}
            ticked={checking}
            onCheck={check}
          >
            {icon}
          </FeedIconCheck>
        ) : (
          <FeedIcon failed={view.failed}>{icon}</FeedIcon>
        )}
      </span>
      <span className="flex min-w-0 flex-col">
        {/* An open card reads its title in full, so a long title wraps; a short one sits where the row drew it,
          and the label follows its first line */}
        <span
          className={cn(
            FEED_ROW_LINE_CLASS,
            'h-auto min-h-[var(--ac-row-line-h)] items-baseline pt-[calc((var(--ac-row-line-h)-var(--ac-row-title-lh))/2)]'
          )}
        >
          <FeedRowTitle
            title={shownTitle}
            struck={struck}
            striking={checking}
            expanded
            wraps
            onOpen={onClose}
            onKeyDown={onKeyDown}
          />
          {label ? (
            <span
              data-testid="feed-frame-label"
              className={FEED_ROW_META_CLASS}
            >
              {label}
            </span>
          ) : null}
          {staffLabel}
          {view.stake ? (
            <span
              className={cn(
                FEED_CHIP_CLASS,
                'min-w-0 shrink-[10] truncate',
                STAKE_PILL_CLASS[view.stake.tone]
              )}
            >
              {view.stake.label}
            </span>
          ) : null}
          {subtasks ? <FeedSubtasksChip progress={subtasks} /> : null}
        </span>
        {description ? (
          <span className={FEED_DESCRIPTION_CLASS}>{description}</span>
        ) : null}
      </span>
      <span className="relative z-[1] -mr-[calc(4*var(--ac-u))] flex h-[var(--ac-row-line-h)] items-center gap-[calc(2*var(--ac-u))]">
        {actions}
        {more}
        {closable && collapses ? (
          <FeedRowAction
            icon={Icons.chevronDown}
            label="Collapse"
            hotkey={['enter']}
            data-feed-chevron="up"
            onClick={onClose}
          />
        ) : closable ? (
          <FeedRowAction icon={Icons.cancel} label="Close" onClick={onClose} />
        ) : null}
      </span>
    </div>
  );
};
